import React, { ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";
import { useAtomValue } from "jotai";
import { OnboardingState } from "@nestcoinco/hyper-api-gateway-api-client";

import "./DefaultOnboardingStatusPage.scss";
import { handleGetOnboardingState } from "../../../api-clients/userAccountClient";
import { OnboardingSteps } from "../../../utils/constants";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import { userAtom } from "../../../jotai/atoms/user";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";

type IDefaultOnboardingStatusPageProps = {
  titleText: ReactNode;
  secondaryText: string;
  primaryImage: string;
  secondaryIcon?: ReactNode;
  redirectDelay?: number;
  redirectPath?: string;
  metaData?: {
    title?: string;
    description?: string;
    image?: string;
  };
  stayOnPage?: boolean;
  nextStep?: OnboardingSteps;
};

function DefaultOnboardingStatusPage({
  titleText,
  secondaryText,
  primaryImage,
  secondaryIcon,
  redirectPath,
  stayOnPage,
  nextStep,
  redirectDelay = 2000,
}: IDefaultOnboardingStatusPageProps) {
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const user = useAtomValue(userAtom);
  const accessToken = user?.tokens?.access;
  const { goToStep } = useOnboardingSteps();

  useEffect(() => {
    if (!accessToken || stayOnPage) return;

    const navigateAfterDelay = (step: OnboardingSteps) => {
      const timeoutId = setTimeout(() => {
        goToStep(step);
        if (redirectPath) navigate(redirectPath);
      }, redirectDelay);

      timeoutRef.current = timeoutId;
    };

    (async () => {
      if (nextStep) {
        navigateAfterDelay(nextStep);
        return;
      }

      const onboardingState = await handleGetOnboardingState();

      if (!onboardingState) {
        navigateAfterDelay(OnboardingSteps.FIND_FRIENDS);
        return;
      }

      const onboardingStep = getOnboardingStep(onboardingState);
      navigateAfterDelay(onboardingStep);

      return () => clearTimeout(timeoutRef.current);
    })();
  }, [
    accessToken,
    goToStep,
    navigate,
    nextStep,
    redirectDelay,
    redirectPath,
    stayOnPage,
  ]);

  return (
    <>
      <OnboardingContainer className="MobileOnly position-relative defaultOnboardingStatusPage">
        <Stack className="main-content">
          <div className="d-flex justify-content-center primaryImageContainer">
            <Image src={primaryImage} className="primaryImage" />
          </div>
          <Stack gap={3} className="align-items-center text-container">
            <h1 className="text-white primary-text">{titleText}</h1>
            <p className="secondary-text d-flex justify-content-center text-center text-white">
              {secondaryText}
            </p>
          </Stack>
        </Stack>

        <div className="d-flex justify-content-center secondaryImageContainer">
          {secondaryIcon}
        </div>
      </OnboardingContainer>
    </>
  );
}

export default DefaultOnboardingStatusPage;

function getOnboardingStep({
  claimedUsername,
  usedReferralCode,
}: OnboardingState): OnboardingSteps {
  switch (true) {
    case claimedUsername:
      return OnboardingSteps.VIEW_WELCOME;
    case usedReferralCode:
      return OnboardingSteps.CLAIM_USERNAME;
    default:
      return OnboardingSteps.CLAIM_USERNAME;
  }
}
